import env from 'src/env'
import { getErrorMessage } from '../error'
import { slashless } from '../string'
import { urlWithParams } from '../urlUtils'
import { GetHttpInitOptions, HttpFunctionOptions } from './types'

const getAuthHeader = () => {
  const token = localStorage.getItem('token')
  return token ? `Bearer ${token}` : undefined
}

/**
 * Returns an request init object with the given method and headers
 * Adds the Authorization header with the token if it exists (localStorage's `token`)
 */
export const getHttpInit = (
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  body?: string,
  options?: GetHttpInitOptions,
): RequestInit => {
  const withAuth = options?.withAuth ?? true

  const authHeader = withAuth ? getAuthHeader() : undefined

  const init: RequestInit = {
    method,
    headers: {
      Accept: '*/*',
      'Content-Type': 'application/json',
      ...(authHeader && { Authorization: authHeader }),
    },
    body,
  }

  return init
}

/**
 * Builds the API URL for the given path.
 * Supports URL parameters.
 */
export const apiUrl = (
  path: string,
  params: Record<string, string> = {},
  options: ApiUrlOptions = {},
): string => {
  const prefix = slashless(options?.prefix ?? 'api')

  const baseUrl = [env.API_URL, prefix].filter(Boolean).join('/')

  const slashlessPath = slashless(path)

  const url = [baseUrl, slashlessPath].filter(Boolean).join('/')

  return urlWithParams(url, params, options)
}

const createApiHttpFunctionWithBody =
  (method: 'PUT' | 'POST' | 'PATCH') =>
  async <ResBody>(path: string, options?: HttpFunctionOptions): Promise<ResBody> => {
    const url = apiUrl(path, options?.params, { prefix: options?.urlPrefix })

    const withAuth = options?.withAuth ?? true

    const { headers: baseHeaders } = getHttpInit(method, undefined, { withAuth })

    const init: RequestInit = {
      method,
      headers: { ...baseHeaders, ...options?.headers },
      ...(options?.body && { body: JSON.stringify(options.body) }),
    }

    const response = await fetch(url, init)

    const unknownResData = await response.json()

    if (!response.ok) {
      const message = getErrorMessage(unknownResData)
      throw new Error(message)
    }

    return unknownResData as ResBody
  }

export const [apiPost, apiPut] = (['POST', 'PUT'] as const).map(createApiHttpFunctionWithBody)

/**
 * Builds the API URL for the given path.
 * Does not add the `/api` prefix.
 * Supports URL parameters.
 */
export const apiUrlWithoutPrefix = (
  path: string,
  params: Record<string, string> = {},
  options: UrlWithParamsOptions = {},
): string => apiUrl(path, params, { ...options, prefix: '' })
